import { Box, Chip, Divider, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../contexts/ThemeContext";
import { useIsMobile } from "../utils/Functions";
import GoBack from "./buttons/GoBack";

export default function ChipTabs({
  lsKey = null,
  tabLabels = [],
  tabContent = [],
  icons = [],
  tabKeys = null,
  tabTestIds = [],
  goBack = false,
  lastIndex = 0,
  setLastIndexFuntion,
  pushRight = 0,
  contentAlign = "left", //Only works if there is a single chip
  backTo = -1,
  divider = true,
  onTabChange,
  wrapperMaxWidth="100vw"
}) {
  
  const { t } = useTranslation();
  const { mode } = React.useContext(ThemeContext);

  var right = pushRight;
  
  if (pushRight === 0) {
    right = tabContent.length;
  }

  //Memoizing to minimize unneccessary rerenders of storageKey
  const storageKey = useMemo(() => `tabSelection-${lsKey}`, [lsKey]);

  const [selectedIndex, setSelectedIndex] = useState(() => {
    const savedIndex = localStorage.getItem(storageKey);
    if (savedIndex !== null) {
      const parsedIndex = parseInt(savedIndex, 10);
      if (!isNaN(parsedIndex)) {
        if (parsedIndex < 0) {
          return 0;
        }
        //Using this to avoid accidental crashes if tab length changes or is accidentally saved as a negative number
        return Math.max(0, Math.min(lastIndex, tabLabels.length - 1));
      }
    }
    //Fallback to 0 or lastIndex if passed down
    return lastIndex;
  });

  useEffect(() => {
    const index = selectedIndex < 0 ? 0 : selectedIndex;
    localStorage.setItem(storageKey, index.toString());
  }, [selectedIndex, storageKey]);

  const handleTabChange = (index) => {
    const selected = Math.max(0, Math.min(index, tabLabels.length - 1));
    setSelectedIndex(selected);
    localStorage.setItem(storageKey, selected.toString());

    if (typeof setLastIndexFuntion === "function") {
      setLastIndexFuntion(selected);
    }

    var selectedLabel = tabLabels[selected];
    if (tabKeys != null) {
      selectedLabel = selectedLabel + "_" + tabKeys[selected];
    }

    if (onTabChange) {
      onTabChange(selected, selectedLabel);
    }
  };

  const leftChips = tabLabels.slice(0, right);
  const rightChips = tabLabels.slice(right);

  if (leftChips.length == 1 && rightChips.length == 0) {
    
    const justify =
      String(contentAlign).toLowerCase() === "right"
        ? "flex-end"
        : "flex-start";

    return (
      <Wrapper
        divider={divider}
        goBack={goBack}
        selectedIndex={selectedIndex}
        mode={mode}
        tabContent={tabContent}
        maxWidth={wrapperMaxWidth}
      >
        {leftChips.length > 0 ? (
          <Box
            sx={{
              width: "100%",
              flex: 1,
              display: "flex",
              justifyContent: justify,
            }}
          >
            {leftChips.map((label) => (
              <Chip
                key={0}
                icon={icons[0]}
                label={t(label)}
                data-testid={tabTestIds[0]} 
                sx={{
                  p: 2,
                }}
                className={"Mui-selected"}
              />
            ))}
          </Box>
        ) : null}
      </Wrapper>
    );
  }
  
  return (
    <Wrapper
      divider={divider}
      goBack={goBack}
      selectedIndex={selectedIndex}
      mode={mode}
      tabContent={tabContent}
      maxWidth={wrapperMaxWidth}
    >
      {leftChips.map((label, index) => (
        <Chip
          key={index}
          icon={icons[index]}
          label={t(label)}
          data-testid={tabTestIds[index]}
          onClick={() => handleTabChange(index)}
          sx={{
            p: 2,
            flexShrink: 0,
          }}
          className={selectedIndex === index ? "Mui-selected" : ""}
          clickable
        />
      ))}
      {rightChips.length > 0 && <Box sx={{ flexGrow: 1, minWidth: 8 }} />}
      {
          rightChips.map((label, index) => (
            <Chip
              key={leftChips.length + index}
              icon={icons[leftChips.length + index]}
              label={t(label)}
              data-testid={tabTestIds[leftChips.length + index]}
              sx={{
                p: 2,
              ...(index === 0 && { marginLeft: "auto" }),
              }}
              onClick={() => handleTabChange(leftChips.length + index)}
              className={
                selectedIndex === leftChips.length + index ? "Mui-selected" : ""
              }
              clickable
            />
          )
      )}
    </Wrapper>
  );
}

function Wrapper({
  children,
  mode,
  goBack,
  divider,
  tabContent,
  selectedIndex,
  maxWidth="100vw"
}) {
  const navigate = useNavigate();
  const isMobile=useIsMobile();
  return (
    <Box>
      <Box
        className={"custom-scrollbar" + "-" + mode}
        sx={{
          maxWidth: {maxWidth},
          display: "flex",
          alignItems: "center",
          overflowX: "auto",
          whiteSpace: "nowrap",
          gap: 1,
          paddingBottom: 1,
          px: isMobile ? 2 : 0,
          justifyContent: isMobile ? "space-evenly" : null,
        }}
      >
        {goBack && <GoBack/>}
        
        {children}

        {divider && <Divider />}
      </Box>

      <Box mt={4}>
        <Typography variant="h6">{tabContent[selectedIndex]}</Typography>
      </Box>
    </Box>
  );
}
